import React, { useState, useEffect, useCallback, useMemo } from 'react'

import {
  Card,
  Icon,
  Rating,
  InfoOutlined,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string,
  bool,
  number,
  arrayOf,
  oneOf,
  shape,
} from 'prop-types'

import PreviewEditButton from 'Pods/ItemPage/Components/PreviewEditButton/PreviewEditButton'
import WishlistButton from 'Pods/ItemPage/Components/WishlistButton/WishlistButton'
import { usePreviewMode } from 'Pods/ItemPage/context/PreviewModeContext'
import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import { availableLicenses } from 'Pods/Shared/availableLicenses'
import useGon from 'Pods/Shared/Hooks/useGon'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import i18n from 'Pods/Shared/I18n'

import checkDownloadAvailabilityAndGetUrl from 'Utils/checkDownloadAvailabilityAndGetUrl'
import downloadLimitMessage from 'Utils/downloadLimitMessage'
import { formatLongDate } from 'Utils/FormatDate'
import formatPrice from 'Utils/formatPrice'
import resolveProductPricing from 'Utils/ResolveProductPricing'
import showErrorNotification from 'Utils/showErrorNotification'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import AddToCartButton from './Components/AddToCartButton/AddToCartButton'
import BuyNowButton from './Components/BuyNowButton/BuyNowButton'
import CommentButton from './Components/CommentButton/CommentButton'
import FreeDownloadButton from './Components/FreeDownloadButton/FreeDownloadButton'
import IncludedPurchase from './Components/IncludedPurchase/IncludedPurchase'
import LicenseDropdownLoader from './Components/LicenseDropdownLoader/LicenseDropdownLoader'
import LicenseModalLoader from './Components/LicenseModalLoader/LicenseModalLoader'
import ModelNotAvailableButton from './Components/ModelNotAvailableButton/ModelNotAvailableButton'
import MyPurchasesButton from './Components/MyPurchasesButton/MyPurchasesButton'
import OnetimePurchase from './Components/OnetimePurchase/OnetimePurchase'
import PricingTag from './Components/PricingTag/PricingTag'
import ProceedToCheckoutButton from './Components/ProceedToCheckoutButton/ProceedToCheckoutButton'
import ReviewButton from './Components/ReviewButton/ReviewButton'
import deriveSubscriptionWidgetTracking from './Components/SubscriptionPurchase/deriveSubscriptionWidgetTracking'
import useItemPageSubscriptionTracking from './Components/SubscriptionPurchase/hooks/useItemPageSubscriptionTracking'
import PURCHASE_OPTION from './Components/SubscriptionPurchase/purchaseOptions'
import SubscribeButton from './Components/SubscriptionPurchase/SubscribeButton/SubscribeButton'
import SubscriberStatus from './Components/SubscriptionPurchase/SubscriberStatus/SubscriberStatus'
import SubscriptionPurchase from './Components/SubscriptionPurchase/SubscriptionPurchase'
import SubscriptionStatusNotice from './Components/SubscriptionStatusNotice/SubscriptionStatusNotice'
import SubscriptionValueLine from './Components/SubscriptionValueLine/SubscriptionValueLine'
import useBuyNowCleanup from './hooks/useBuyNowCleanup'
import formatPlanPrice from './Utils/formatPlanPrice'
import { MANAGE_SUBSCRIPTION_URL } from './Utils/subscriptionUrls'
import translatePlansRestructure from './Utils/translatePlansRestructure'

const DEFAULT_MOCKED_PRICING_DATA = {
  price: null,
  originalPrice: null,
  discount: null,
  discountAmount: null,
  ribbon: null,
}

// editorial is omitted: it is already a No AI variant in availableLicenses (allowAi: false)
// and shares its feature set with editorial_no_ai, so remapping is a no-op.
// editorial_royalty_free maps to royalty_free_no_ai (not editorial_no_ai) because the variant
// uses RF feature set; mapping to editorial_no_ai would also drop Remixing/Commercial rights.
const FORCE_NO_AI_LICENSE_MAP = {
  royalty_free: 'royalty_free_no_ai',
  editorial_royalty_free: 'royalty_free_no_ai',
  custom: 'custom_no_ai',
}

const INSET_WISHLIST_CLASS = 'pricing-area__actions-button--inset pricing-area__actions-left-button--inset'
const ROYALTY_FREE_LICENSES = ['royalty_free', 'royalty_free_no_ai']

const PLAN_TIERS = ['essentials', 'premium']
const STATUS_NOTICE_STATES = ['paused', 'payment_failed']

function statusNoticeCopy({ state, plan, pausedUntil }, locale) {
  if (state === 'paused') {
    return {
      // pausedUntil is a UTC timestamp; formatting it in UTC keeps the viewer's timezone from shifting the day.
      title: translatePlansRestructure('paused_notice.title', { date: formatLongDate(pausedUntil, locale, 'UTC') }),
      text: translatePlansRestructure('paused_notice.body', {
        plan: translatePlansRestructure(`plan_labels.${PLAN_TIERS.includes(plan) ? plan : 'essentials'}`),
      }),
      linkLabel: translatePlansRestructure('paused_notice.resume'),
    }
  }

  return {
    title: translatePlansRestructure('payment_failed_notice.title'),
    text: translatePlansRestructure('payment_failed_notice.body'),
    linkLabel: translatePlansRestructure('payment_failed_notice.update_payment_method'),
  }
}

function allowanceNoticeCopy(resetsAt, plan, locale) {
  // resetsAt is a UTC timestamp; formatting it in UTC keeps the viewer's timezone from shifting the day.
  const date = resetsAt &&
    new Date(resetsAt).toLocaleDateString(locale, { month: 'long', day: 'numeric', timeZone: 'UTC' })

  return {
    title: translatePlansRestructure('allowance_notice.title'),
    text: date
      ? translatePlansRestructure('allowance_notice.body', { plan, date })
      : translatePlansRestructure('allowance_notice.body_without_date'),
    linkLabel: translatePlansRestructure('allowance_notice.manage'),
  }
}

function PricingArea({
  title,
  price,
  license,
  customLicense = '',
  publishedAiFriendly = false,
  free,
  reviewsCount,
  ratingScore,
  badges = EMPTY_ARRAY,
  formatsCount = 0,
  productId,
  locale = 'en',
  mockedPricingData = DEFAULT_MOCKED_PRICING_DATA,
  mockedIsInCart = false,
  mockedIsProductOwned = false,
  mockedShowSubscription = false,
  mockedIsActiveSubscriber = false,
  mockedSubscriptionLimitReached = false,
  extractingCoreStyles = false,
  subscriptionDownloadsPerMonth,
  subscriptionCatalogValue,
  subscriptionTierBand = 'essentials',
  subscriptionBullets = EMPTY_ARRAY,
  subscriptionSubscribed = false,
  subscriptionPricePerModel,
  subscriptionPerModelLabel,
  subscriptionFromPriceLabel,
  subscriptionPaymentFormUrl = '/profile/subscription/payment-form',
  subscriptionUpgradeUrl = '/profile/subscription/upgrade',
  subscriptionV17CatalogEnabled = false,
  planName,
  plansRestructureEnabled = false,
  subscriptionMonthlyPrice = null,
  subscriptionGenerationsPerMonth = null,
  subscriptionAnnualFreeMonths = null,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const { isPreview } = usePreviewMode()
  const [isRestricted, setIsRestricted] = useState()
  const [isDropDownActive, setIsDropDownActive] = useState(false)
  const [isProductOwned, setIsProductOwned] = useState(extractingCoreStyles ? mockedIsProductOwned : false)
  const [isFreeItemDownloaded, setIsFreeItemDownloaded] = useState(false)
  const [subscriptionDownloadState, setSubscriptionDownloadState] = useState(null)
  const [isInCart, setIsInCart] = useState(extractingCoreStyles ? mockedIsInCart : false)
  const [productPricing, setProductPricing] = useState(extractingCoreStyles ? mockedPricingData : {
    price,
    originalPrice: price,
    discount: null,
    discountAmount: null,
    ribbon: null,
  })
  const [isLicenseModalActive, setIsLicenseModalActive] = useState(false)
  const [userSelectedOption, setUserSelectedOption] = useState(null)
  const [isCheckingDownload, setIsCheckingDownload] = useState(false)
  const [isDownloadLimitReached, setIsDownloadLimitReached] = useState(false)

  const pageConfig = usePageConfig()
  const gon = useGon()
  const { restricted } = gon || {}
  const isLoggedIn = pageConfig?.isCurrentUser || false

  const {
    enableBuyNow,
    isActiveSubscriber: pageConfigActiveSubscriber,
    subscriptionTier,
    subscriptionScheduledForCancellation,
    subscriptionLimitReached: pageConfigSubscriptionLimitReached,
    subscriptionFlowEnabled,
    forceNoAiLicense = false,
    subscriptionAllowance,
  } = pageConfig || {}

  // Extraction renders one subscribed and one non-subscribed variant from mocks (see
  // PricingAreaWrapper): JS is blocked there, so pageConfig never resolves.
  const isActiveSubscriber = extractingCoreStyles
    ? mockedIsActiveSubscriber
    : pageConfigActiveSubscriber

  const purchaseOption = userSelectedOption ?? PURCHASE_OPTION.SUBSCRIPTION

  const displayLicenseId = forceNoAiLicense
    ? (FORCE_NO_AI_LICENSE_MAP[license] || license)
    : license

  const licenseConfig = availableLicenses.find((item) => item.id === displayLicenseId) || {
    id: displayLicenseId,
    title: 'License',
    allowAi: false,
    features: [],
  }
  const isCustomLicense = !forceNoAiLicense && ['custom', 'custom_no_ai'].includes(license)

  const isSubscriptionEnabled = extractingCoreStyles
    ? mockedShowSubscription
    : subscriptionFlowEnabled

  const premiumExposed = !!pageConfig?.premiumExposed
  const isPremiumBandItem = subscriptionV17CatalogEnabled && subscriptionTierBand === 'premium'

  // An active subscription's tier covers this item when the viewer is Premium (covers both bands)
  // or the item is not Premium-band. Mirrors Subscription#covers_item? on the backend, computed
  // from the real catalog band (a fact about entitlement, independent of the rollout experiment),
  // so an Essentials subscriber viewing a Premium-band item is NOT covered and must upgrade or buy
  // it once.
  const subscriptionTierCoversItem =
    isActiveSubscriber && (subscriptionTier === 'premium' || !isPremiumBandItem)

  // With no credits left the subscriber cannot download this item, so every subscription
  // affordance would dead-end on a CTA that 403s. Drop back to the plain purchase UI - matching
  // the backend, which lets them add a covered item to the cart and buy it outright while they are
  // out of credits. Scoped to items their tier already covers: an Essentials subscriber looking at
  // a Premium-band item keeps the upgrade offer, since upgrading is what unlocks it.
  const subscriptionLimitReached = extractingCoreStyles
    ? mockedSubscriptionLimitReached
    : pageConfigSubscriptionLimitReached

  const subscriptionOutOfCredits = subscriptionTierCoversItem && !!subscriptionLimitReached

  const subscriptionCoversItem = subscriptionTierCoversItem && !subscriptionOutOfCredits

  // Premium buyer-surface gate (subscription_v1_7 kill switch): it gates only the OFFER of Premium
  // (upsell / subscribe / upgrade). It must NEVER hide an existing subscriber's entitlement, so an
  // active subscription that already covers the item bypasses the gate. With the switch off, users
  // who don't cover the item get the Essentials-only world (no Premium offer). Resolved from
  // pageConfig client-side (fast page), riding the same hydration boundary as the rest of the
  // subscription UI (subscriptionFlowEnabled). Keyed on tier coverage, not the credit balance:
  // running out of downloads is transient and must not revoke the exemption.
  const premiumBandExposed = !isPremiumBandItem || premiumExposed || subscriptionTierCoversItem

  const subscriptionOfferable =
    isSubscriptionEnabled &&
    subscriptionSubscribed &&
    premiumBandExposed &&
    !free &&
    !isPreview

  // A paused or past_due / redemption subscriber is not active, so the block would offer a new subscription that
  // the checkout refuses (blocking_new_checkout). They keep the purchase actions and, when their plan covers the
  // item, a notice to resume or to fix the payment method.
  const allowanceState = subscriptionAllowance?.state
  const inactiveSubscriptionState = plansRestructureEnabled && STATUS_NOTICE_STATES.includes(allowanceState)
    ? allowanceState
    : null
  const inactivePlanCoversItem = subscriptionAllowance?.plan === 'premium' || !isPremiumBandItem

  const canShowSubscriptionUI = subscriptionOfferable && !isProductOwned && !isRestricted &&
    !subscriptionOutOfCredits && !inactiveSubscriptionState

  // subscription_v1_7_catalog gates whether the Premium tier exists at all; premiumBandExposed
  // gates the per-user rollout. With the catalog flag off the server already drops Premium-band
  // items from the offering (subscription_subscribed_for_ui? -> eligible_for_subscription?), and
  // a control user collapses the item to the Essentials band with no Premium tier in the redirect.
  const effectiveTierBand =
    premiumBandExposed && subscriptionV17CatalogEnabled ? subscriptionTierBand : 'essentials'

  // An active Essentials subscriber on a Premium-band item upgrades their plan (goes to the
  // upgrade checkout) instead of starting a fresh subscription at the payment form.
  const isEssentialsToPremiumUpgrade =
    isActiveSubscriber && subscriptionTier === 'essentials' && effectiveTierBand === 'premium'

  const showSubscriptionFlow = canShowSubscriptionUI && !subscriptionCoversItem

  const restructurePlan = useMemo(() => {
    if (!plansRestructureEnabled || subscriptionMonthlyPrice === null) return null

    return {
      monthlyPrice: subscriptionMonthlyPrice,
      generationsPerMonth: subscriptionGenerationsPerMonth,
      annualFreeMonths: subscriptionAnnualFreeMonths,
      catalogValue: subscriptionCatalogValue,
      isUpgrade: isEssentialsToPremiumUpgrade,
    }
  }, [
    plansRestructureEnabled, isEssentialsToPremiumUpgrade, subscriptionMonthlyPrice, subscriptionGenerationsPerMonth,
    subscriptionAnnualFreeMonths, subscriptionCatalogValue,
  ])
  const showSubscriptionValueLine = Boolean(
    (pageConfig || extractingCoreStyles) && showSubscriptionFlow && restructurePlan &&
      purchaseOption === PURCHASE_OPTION.SUBSCRIPTION
  )
  const showSubscriberBenefits = canShowSubscriptionUI && subscriptionCoversItem
  const showAllowanceNotice = plansRestructureEnabled && subscriptionOfferable && subscriptionOutOfCredits &&
    !isProductOwned && !isRestricted
  const subscriberTier = PLAN_TIERS.includes(subscriptionTier) ? subscriptionTier : 'essentials'
  const subscriberPlanLabel = translatePlansRestructure(`plan_labels.${subscriberTier}`)
  const useLegacyCard = !plansRestructureEnabled || free
  const showOnetimePurchase = ROYALTY_FREE_LICENSES.includes(displayLicenseId) && !free && !isProductOwned &&
    !isRestricted && !isPreview && !isInCart && !enableBuyNow && !showSubscriptionFlow && !showSubscriberBenefits
  const showStatusNotice = Boolean(inactiveSubscriptionState) && inactivePlanCoversItem && subscriptionOfferable &&
    !isProductOwned && !isRestricted

  let statusNotice = null
  if (showStatusNotice) {
    statusNotice = {
      tone: inactiveSubscriptionState === 'paused' ? 'neutral' : 'error',
      ...statusNoticeCopy(subscriptionAllowance, locale),
    }
  } else if (showAllowanceNotice) {
    statusNotice = {
      tone: 'neutral',
      ...allowanceNoticeCopy(subscriptionAllowance?.resetsAt, subscriberPlanLabel, locale),
    }
  }

  let subscribeCtaKey = 'subscription_item_page.subscribe_cta'
  if (restructurePlan?.isUpgrade) {
    subscribeCtaKey = 'subscription_item_page.plans_restructure.upgrade_cta'
  } else if (isEssentialsToPremiumUpgrade) {
    subscribeCtaKey = 'subscription_item_page.upgrade_cta'
  }

  const showSubscriptionLicense = subscriptionCoversItem && subscriptionSubscribed
  const effectiveLicenseConfig = showSubscriptionLicense
    ? availableLicenses.find((item) => item.id === 'royalty_free_no_ai')
    : licenseConfig
  const effectiveLicense = showSubscriptionLicense ? 'royalty_free_no_ai' : displayLicenseId
  const showAiContactNote = forceNoAiLicense && publishedAiFriendly && !showSubscriptionLicense

  const showRedownloadNote = subscriptionDownloadState === 'redownload' && isProductOwned && !isPreview
  const showResubscribeLink = subscriptionDownloadState === 'resubscribe' && isSubscriptionEnabled && !isPreview

  useEffect(() => {
    if (!pageConfig) {
      return
    }

    const { cart, product, isCurrentUser } = pageConfig
    const { itemIds = [] } = cart || {}
    const { isDownloaded, isPurchased, id, subscriptionDownloadState: downloadState } = product || {}

    if (extractingCoreStyles) {
      return
    }

    if (cart) {
      setIsInCart(itemIds.includes(productId))
    }

    setSubscriptionDownloadState(downloadState ?? null)

    if (isDownloaded && free) {
      setIsFreeItemDownloaded(true)
    } else if (isPurchased) {
      setIsProductOwned(true)
    }

    if (product && cart) {
      const pricingData = resolveProductPricing({
        cart,
        attributes: product,
        id,
        isCurrentUser,
      })
      setProductPricing(pricingData)
    }
  }, [pageConfig, productId, free])

  useEffect(() => {
    setIsRestricted(restricted)
  }, [restricted])

  useEffect(() => {
    const removeCartItemId = (data) => {
      const { attributes } = data || {}
      const { itemId } = attributes || {}
      if (itemId === productId) {
        setIsInCart(false)
      }
    }

    App.events.subscribe('app:cart:item:remove', removeCartItemId)

    return () => {
      App.events.unsubscribe('app:cart:item:remove', removeCartItemId)
    }
  }, [productId])

  const subscriptionTracking = useMemo(
    () => deriveSubscriptionWidgetTracking({
      price, isLoggedIn, isActiveSubscriber, subscriptionTier, subscriptionTierBand: effectiveTierBand,
      subscriptionScheduledForCancellation,
    }),
    [price, isLoggedIn, isActiveSubscriber, subscriptionTier, effectiveTierBand, subscriptionScheduledForCancellation]
  )
  const { trackWidgetVisible, trackCtaClick } = useItemPageSubscriptionTracking({
    itemId: productId,
    trackingPayload: subscriptionTracking,
  })

  useBuyNowCleanup(productId, enableBuyNow)

  const handleOpenLicenseModal = () => {
    setIsLicenseModalActive(true)
  }

  const handleCloseLicenseModal = () => {
    setIsLicenseModalActive(false)
  }

  const handleToggleDropDown = useCallback(() => {
    if (isCustomLicense) {
      handleOpenLicenseModal()
      return
    }

    setIsDropDownActive(state => !state)
  }, [isCustomLicense])

  const handleRatingClick = useCallback((event) => {
    event.preventDefault()
    window.dispatchEvent(new CustomEvent('open-review-modal', {
      detail: { filterBy: 'positive' },
    }))
  }, [])

  const handleFreeDownloadClick = async () => {
    if (isCheckingDownload || isDownloadLimitReached) {
      return
    }

    if (!isLoggedIn) {
      App.events.publish('app:login:open')
      return
    }

    const path = showSubscriberBenefits ? 'subscription-download-page' : 'download-page'
    const destinationUrl = `/items/${productId}/${path}`

    setIsCheckingDownload(true)

    try {
      const response = await checkDownloadAvailabilityAndGetUrl({
        itemId: productId,
        destinationUrl,
      })

      if (response.status === 'ok' && response.downloadUrl) {
        window.location.href = response.downloadUrl
        return
      }

      if (response.status === 'limit_reached') {
        setIsDownloadLimitReached(true)
        showErrorNotification(downloadLimitMessage(response))
        return
      }

      showErrorNotification('Something went wrong, please try again')
    } catch {
      showErrorNotification('Something went wrong, please try again')
    } finally {
      setIsCheckingDownload(false)
    }
  }

  const handleSubscriberDownloadClick = () => {
    trackCtaClick('download')
    handleFreeDownloadClick()
  }

  const hasPriceDiscount = productPricing?.discount > 0
  const isDiscountRibbon = ['saleOff', 'CLD'].includes(productPricing?.ribbon)
  const shouldShowDiscount = isDiscountRibbon && hasPriceDiscount

  const renderPriceSection = () => {
    if (free) {
      if (!plansRestructureEnabled) return null

      return (
        <div className="pricing-area__price-container">
          <span className="pricing-area__price-text">{translatePlansRestructure('free.price')}</span>
        </div>
      )
    }

    return (
      <div className="pricing-area__price-container">
        <span className="pricing-area__price-text">
          {formatPrice(productPricing?.price)}
        </span>
        {shouldShowDiscount && (
          <div className="pricing-area__price-discount-container">
            <span className="pricing-area__price-discount-text">
              {formatPrice(productPricing.originalPrice)}
            </span>
            <span className="pricing-area__discount-percentage">
              -{Math.round(productPricing.discount * 100)}%
            </span>
          </div>
        )}
      </div>
    )
  }

  const renderLicenseText = () => {
    if (!effectiveLicenseConfig.allowAi) {
      return (
        <span
          className="pricing-area__license-text"
          onClick={handleToggleDropDown}
        >
          {effectiveLicenseConfig.title}{' '}
          <span className="pricing-area__license-text-ai">(no AI)</span>
        </span>
      )
    }

    return (
      <span className="pricing-area__license-text">
        {effectiveLicenseConfig.title}
      </span>
    )
  }

  const handleAddToCartSuccess = () => {
    setIsInCart(true)
  }

  const renderActionButtons = (isInset = false) => {
    if (!pageConfig && !extractingCoreStyles) {
      return <div className="pricing-area__actions-placeholder" />
    }

    if (isPreview) {
      return (
        <>
          <WishlistButton productId={productId} disabled />
          {free ? (
            <FreeDownloadButton disabled />
          ) : (
            <AddToCartButton productId={productId} disabled />
          )}
        </>
      )
    }

    if (isFreeItemDownloaded) {
      return (
        <>
          <FreeDownloadButton
            onClick={handleFreeDownloadClick}
            isDownloaded
            disabled={isCheckingDownload || isDownloadLimitReached}
            isLoading={isCheckingDownload}
          />
          <CommentButton />
        </>
      )
    }

    if (isProductOwned) {
      return (
        <>
          <MyPurchasesButton />
          <ReviewButton />
        </>
      )
    }

    if (free && plansRestructureEnabled) {
      return (
        <>
          <WishlistButton productId={productId} />
          <FreeDownloadButton
            onClick={handleFreeDownloadClick}
            disabled={isCheckingDownload || isDownloadLimitReached}
            isLoading={isCheckingDownload}
            variant="primary"
            label={translatePlansRestructure('free.download')}
            showIcon
            isPaired
          />
        </>
      )
    }

    if (free) {
      return (
        <>
          {extractingCoreStyles && <div className="pricing-area__actions-placeholder" />}
          <FreeDownloadButton
            onClick={handleFreeDownloadClick}
            disabled={isCheckingDownload || isDownloadLimitReached}
            isLoading={isCheckingDownload}
          />
        </>
      )
    }

    if (isRestricted) {
      return (
        <>
          <WishlistButton productId={productId} />
          <ModelNotAvailableButton />
        </>
      )
    }

    if (showSubscriberBenefits) {
      return (
        <FreeDownloadButton
          onClick={handleSubscriberDownloadClick}
          label="Download"
          showIcon
          isInset={isInset}
          disabled={isCheckingDownload || isDownloadLimitReached}
          isLoading={isCheckingDownload}
        />
      )
    }

    if (showSubscriptionFlow && purchaseOption === PURCHASE_OPTION.SUBSCRIPTION) {
      return (
        <SubscribeButton
          paymentFormPath={isEssentialsToPremiumUpgrade ? subscriptionUpgradeUrl : subscriptionPaymentFormUrl}
          label={i18n.t(subscribeCtaKey, { ns: 'rails' })}
          onClick={(navigate) => trackCtaClick(isEssentialsToPremiumUpgrade ? 'upgrade' : 'new_subscribe', navigate)}
          isInset={isInset}
        />
      )
    }

    if (enableBuyNow) {
      if (isInCart) {
        return <ProceedToCheckoutButton isInset={isInset} />
      }

      return (
        <>
          <AddToCartButton
            productId={productId}
            onSuccess={handleAddToCartSuccess}
            variant="secondary"
            isInset={isInset}
          />
          <BuyNowButton productId={productId} isInset={isInset} />
        </>
      )
    }

    return (
      <>
        <WishlistButton productId={productId} customClass={isInset ? INSET_WISHLIST_CLASS : undefined} />
        {isInCart ? (
          <ProceedToCheckoutButton isInset={isInset} />
        ) : (
          <AddToCartButton
            productId={productId}
            onSuccess={handleAddToCartSuccess}
            isInset={isInset}
          />
        )}
      </>
    )
  }

  const renderLicenseRow = () => (
    <div
      className={classnames(
        'pricing-area__license-container',
        free && 'pricing-area__license-container--free-item',
        plansRestructureEnabled && 'pricing-area__license-container--restructure'
      )}
    >
      {renderLicenseText()}
      <div className="pricing-area__license-container-icon">
        <Icon
          svgComponent={InfoOutlined}
          color="dark"
          className="pricing-area__license-icon"
          onClick={handleToggleDropDown}
        />
      </div>
      <LicenseDropdownLoader
        licenseConfig={effectiveLicenseConfig}
        onClose={handleToggleDropDown}
        isActive={isDropDownActive}
        showAiContactNote={showAiContactNote}
        locale={locale}
        onLicenseClick={handleOpenLicenseModal}
      />
    </div>
  )

  const formatBlockPrice = plansRestructureEnabled ? formatPlanPrice : formatPrice

  const renderSubscriptionPurchase = () => (
    <SubscriptionPurchase
      planName={planName}
      subscriptionTierBand={effectiveTierBand}
      subscriptionBullets={subscriptionBullets}
      subscriptionPricePerModel={subscriptionPricePerModel}
      subscriptionPerModelLabel={subscriptionPerModelLabel}
      subscriptionFromPriceLabel={subscriptionFromPriceLabel}
      subscriptionDownloadsPerMonth={subscriptionDownloadsPerMonth}
      subscriptionCatalogValue={subscriptionCatalogValue}
      productPrice={formatBlockPrice(productPricing?.price)}
      productOriginalPrice={formatBlockPrice(productPricing?.originalPrice)}
      hasDiscount={productPricing?.discount > 0}
      selectedOption={purchaseOption}
      onOptionChange={setUserSelectedOption}
      onImpression={trackWidgetVisible}
      subscriptionPlan={restructurePlan}
      plansRestructureEnabled={plansRestructureEnabled}
    />
  )

  const renderPurchaseContent = () => {
    if (showSubscriptionFlow || showSubscriberBenefits) {
      return (
        <>
          {renderLicenseRow()}
          {showSubscriberBenefits
            ? <SubscriberStatus productPrice={formatPrice(productPricing?.price)} />
            : renderSubscriptionPurchase()}
        </>
      )
    }

    return (
      <>
        {renderPriceSection()}
        {renderLicenseRow()}
      </>
    )
  }

  const renderTitleSection = (children = null) => (
    <>
      {badges.length > 0 && (
        <div className="pricing-area__header">
          {badges.map((badge) => (
            <PricingTag key={badge} badge={badge} />
          ))}
        </div>
      )}
      <div className="pricing-area__content">
        <div className="pricing-area__title-wrapper">
          <h1
            className={classnames('pricing-area__title', plansRestructureEnabled && 'pricing-area__title--restructure')}
          >
            {title}
          </h1>
          <PreviewEditButton className="preview-edit-button--title" />
        </div>
        {(!free || plansRestructureEnabled) && reviewsCount > 0 && (
          <div className="pricing-area__rating-container">
            <a
              href="#reviews"
              className="pricing-area__rating-link"
              onClick={handleRatingClick}
            >
              <Rating
                id="pricing-area-rating"
                initialValue={ratingScore}
                numberOfItems={5}
                size="small"
                trail={reviewsCount === 1 ? `(1 review)` : `(${reviewsCount} reviews)`}
                isCheckboxHidden
                className="pricing-area__rating"
                ariaLabelPrefix={is3dPrintModel ? '3D Print Model rating' : 'Model rating'}
                isFractional
              />
            </a>
          </div>
        )}
        {children}
      </div>
    </>
  )

  const licenseModal = (
    <LicenseModalLoader
      isActive={isLicenseModalActive}
      onClose={handleCloseLicenseModal}
      license={effectiveLicense}
      customLicense={customLicense}
      licenseTitle={effectiveLicenseConfig.title}
    />
  )

  const redownloadNote = showRedownloadNote && (
    <p className="pricing-area__subscription-note cgt-caption">
      {i18n.t('subscription_item_page.redownload_note', { ns: 'rails' })}
    </p>
  )

  const resubscribeLink = showResubscribeLink && (
    <a
      href={subscriptionPaymentFormUrl}
      className="pricing-area__resubscribe-link cgt-link-primary-accented"
    >
      {i18n.t('subscription_item_page.resubscribe_prompt', { ns: 'rails' })}
    </a>
  )

  const insetActions = (
    <div className="pricing-area__actions pricing-area__actions--inset">
      {renderActionButtons(true)}
    </div>
  )

  const renderRestructurePurchase = () => {
    if (showSubscriberBenefits) {
      return (
        <IncludedPurchase
          price={productPricing?.price}
          plan={subscriberPlanLabel}
          formatsCount={formatsCount}
          actions={insetActions}
        />
      )
    }

    if (showSubscriptionFlow) {
      return (
        <>
          <div className="pricing-area__content pricing-area__content--purchase pricing-area__content--options">
            {renderSubscriptionPurchase()}
          </div>
          {showSubscriptionValueLine && (
            <SubscriptionValueLine
              plan={restructurePlan}
              downloadsPerMonth={subscriptionDownloadsPerMonth}
              productPrice={productPricing?.price}
            />
          )}
          <div className="pricing-area__actions pricing-area__actions--flush">
            {renderActionButtons()}
          </div>
        </>
      )
    }

    if (showOnetimePurchase) {
      return (
        <>
          <OnetimePurchase
            price={productPricing?.price}
            originalPrice={shouldShowDiscount ? productPricing.originalPrice : null}
            formatsCount={formatsCount}
            isCompact={Boolean(statusNotice)}
          />
          <div
            className={classnames(
              'pricing-area__actions',
              statusNotice
                ? 'pricing-area__actions--inset pricing-area__actions--compact'
                : 'pricing-area__actions--onetime pricing-area__actions--flush',
            )}
          >
            {renderActionButtons(Boolean(statusNotice))}
          </div>
        </>
      )
    }

    return (
      <>
        <div className="pricing-area__content pricing-area__content--purchase">
          {renderPriceSection()}
          {redownloadNote}
        </div>
        <div className="pricing-area__actions pricing-area__actions--flush">
          {renderActionButtons()}
        </div>
      </>
    )
  }

  if (useLegacyCard) {
    return (
      <Card hasBorder isRounded customClass="pricing-area">
        {renderTitleSection(
          <>
            {renderPurchaseContent()}
            {redownloadNote}
          </>
        )}
        <div
          className={classnames('pricing-area__actions', plansRestructureEnabled && 'pricing-area__actions--flush')}
        >
          {renderActionButtons()}
        </div>
        {resubscribeLink}
        {free && plansRestructureEnabled && (
          <p className="pricing-area__free-usage cgt-caption">{translatePlansRestructure('free.usage')}</p>
        )}
        {licenseModal}
      </Card>
    )
  }

  return (
    <Card hasBorder isRounded customClass="pricing-area">
      {renderTitleSection(renderLicenseRow())}
      {renderRestructurePurchase()}
      {statusNotice && <SubscriptionStatusNotice linkHref={MANAGE_SUBSCRIPTION_URL} {...statusNotice} />}
      {resubscribeLink}
      {licenseModal}
    </Card>
  )
}

PricingArea.propTypes = {
  title: string.isRequired,
  price: string.isRequired,
  license: oneOf([
    'custom',
    'custom_no_ai',
    'editorial',
    'editorial_no_ai',
    'editorial_royalty_free',
    'royalty_free',
    'royalty_free_no_ai',
  ]).isRequired,
  customLicense: string,
  publishedAiFriendly: bool,
  locale: string,
  free: bool.isRequired,
  reviewsCount: number.isRequired,
  ratingScore: number.isRequired,
  badges: arrayOf(oneOf([
    'BEST PRICE GUARANTEED',
    'NEW',
    'TOP SELLING',
    'QUALITY VERIFIED',
    '3D PRINT VERIFIED',
    'TOP RATED',
    'AI GENERATED',
    'DESIGNER\'S PICK',
  ])),
  formatsCount: number,
  productId: number.isRequired,
  mockedPricingData: shape({
    price: number,
    originalPrice: number,
    discount: number,
    discountAmount: number,
    ribbon: string,
  }),
  mockedIsInCart: bool,
  mockedIsProductOwned: bool,
  mockedShowSubscription: bool,
  mockedIsActiveSubscriber: bool,
  mockedSubscriptionLimitReached: bool,
  extractingCoreStyles: bool,
  subscriptionDownloadsPerMonth: number.isRequired,
  subscriptionCatalogValue: string.isRequired,
  subscriptionTierBand: string,
  subscriptionBullets: arrayOf(string),
  subscriptionSubscribed: bool,
  subscriptionPricePerModel: string.isRequired,
  subscriptionPerModelLabel: string.isRequired,
  subscriptionFromPriceLabel: string.isRequired,
  subscriptionPaymentFormUrl: string,
  subscriptionUpgradeUrl: string,
  subscriptionV17CatalogEnabled: bool,
  planName: string.isRequired,
  plansRestructureEnabled: bool,
  subscriptionMonthlyPrice: number,
  subscriptionGenerationsPerMonth: number,
  subscriptionAnnualFreeMonths: number,
}

export default PricingArea
